/**
 * `TabHistory`: the owner's Back rule for a menu of tabs (2026-10-02), on the
 * shell's own history stack. `AppFrame` keeps one in step with its `active`
 * tab; a game whose menu unmounts under a takeover holds one itself (below).
 *
 * ## The rule
 *
 * - **Home is the root.** Nothing is armed there, and Back on Home does what
 *   the platform does (leaves the page, or closes the installed app).
 * - **Leaving Home arms one entry.** Back on any other tab pops it and shows
 *   Home: it never closes the app.
 * - **Tabs never stack.** Switching between tabs that are not Home re-targets
 *   the same entry, so three tab presses are still one Back from Home.
 * - **Pressing Home gives the entry back** (walked back through the stack, so
 *   the browser's depth and the stack agree).
 *
 * | what happens                       | the entry               |
 * | ---------------------------------- | ----------------------- |
 * | `show(other)` from Home            | pushed                  |
 * | `show(other)` from another tab     | kept (re-targeted)      |
 * | `show(home)`                       | released (walked back)  |
 * | Back on a tab that is not Home     | popped; `onreturn(tab)` |
 * | `dispose()`                        | released                |
 *
 * Everything a tab opens (a game, a sub-screen, a sheet) is an entry of its
 * own *above* this one, so Back unwinds it to the tab and then to Home.
 *
 * ## Who holds it
 *
 * A menu that stays mounted while its takeovers are up needs nothing: give
 * `AppFrame` no `tabHistory` and it makes and disposes its own. A menu that
 * unmounts while a game is up (TwinTen's) must keep the entry alive under the
 * game, so the game holds the `TabHistory` at app level, passes it to
 * `AppFrame`, and may call `show()` itself when it lands back on a tab; every
 * call is idempotent.
 *
 * Plain TypeScript with the push function injectable, like `BackGuard`, so the
 * table is tested against the real stack in jsdom.
 */
import type { PushOverlay } from './backGuard.js';
import { pushOverlay } from './historyStack.js';

/** What a {@link TabHistory} is made with. */
export interface TabHistoryOptions {
  /** The root tab's id: the one Back returns to, where nothing is armed. */
  readonly home: string;
  /**
   * A real Back popped the entry: show `home` now (set the active tab). Told
   * which tab Back was pressed on.
   */
  readonly onreturn: (from: string) => void;
  /** The history stack to arm on; the shell's `pushOverlay` by default. */
  readonly push?: PushOverlay | undefined;
}

export class TabHistory {
  /** The root tab's id. */
  readonly home: string;
  private current: string;
  private release: (() => void) | null = null;
  private disposed = false;
  private readonly onreturn: (from: string) => void;
  private readonly push: PushOverlay;

  constructor(options: TabHistoryOptions) {
    this.home = options.home;
    this.current = options.home;
    this.onreturn = options.onreturn;
    this.push = options.push ?? pushOverlay;
  }

  /** True while Back would return Home (an entry is armed). */
  get armed(): boolean {
    return this.release !== null;
  }

  /** The tab last shown, as far as this history knows. */
  get tab(): string {
    return this.current;
  }

  /** The tab now showing. Call it on every change; repeating it changes nothing. */
  show(tab: string): void {
    if (this.disposed) return;
    this.current = tab;
    if (tab === this.home) {
      this.disarm();
      return;
    }
    if (this.release !== null) return; // Re-targeted: the one entry stays.
    this.release = this.push(() => {
      // A real Back popped the entry: there is nothing left to release.
      this.release = null;
      const from = this.current;
      this.current = this.home;
      this.onreturn(from);
    });
  }

  /** The menu is going away for good: give the entry back. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disarm();
  }

  private disarm(): void {
    const release = this.release;
    this.release = null;
    release?.();
  }
}
